import { useCallback } from 'react';
import { useReactFlow } from 'reactflow';
import { NodeId } from 'web';
import { zip } from 'lodash';
import { Node } from '@/types/node';
import {
  getAbsolutePositionFromRelative,
  getAdjacentPositionForNodeDuplication,
  getNodesBounds,
} from '@/components/Nodes/utils/nodeUtils';
import { useFlowView, useGroupsManagerView } from '@/components/Recipe/FlowContext';
import { useWorkflowStore } from '@/state/workflow.state';
import { UndoRedoEntry } from '@/components/Recipe/undoRedo';
import { useReactFlowViewportPositions } from './useReactFlowViewportPositions';

export function useNodeDuplication() {
  const { getCenterPosition, isNodeInViewport } = useReactFlowViewportPositions();
  const { setViewport, getViewport } = useReactFlow();
  const getReactFlowContainerRect = useWorkflowStore((s) => s.getReactFlowContainerRect);
  const groupManagerView = useGroupsManagerView();
  const flowView = useFlowView();

  const positionDuplicatedNodes = useCallback(
    (originalNodes: Node[], duplicatedNodesIds: NodeId[]): UndoRedoEntry => {
      const undoEntry = UndoRedoEntry.empty();

      if (originalNodes.length === 0) {
        return undoEntry;
      }

      const newNodeIdByOriginal = Object.fromEntries(
        zip(
          originalNodes.map((node) => node.id),
          duplicatedNodesIds,
        ) as [NodeId, NodeId][],
      );

      // Position nodes that are in groups if parent isn't duplicated as well
      undoEntry.add(groupManagerView.positionDuplicatedChildrenInExistingGroups(originalNodes, duplicatedNodesIds));

      const originalNodesNotInGroups = originalNodes.filter((node) => !flowView.isNodeInGroup(node));

      if (originalNodesNotInGroups.length === 0) {
        return undoEntry;
      }

      const origTopLeftNode = findTopLeftNode(originalNodesNotInGroups);

      // Position the top-left node: if any node is outside viewport, center the viewport on the top-left node, otherwise position the top-left node adjacent to the original child node
      const isAnyNodeOutsideViewport = originalNodesNotInGroups.some((node) => !isNodeInViewport(node));
      const newTopLeftPosition = isAnyNodeOutsideViewport
        ? getCenterPosition()
        : getAdjacentPositionForNodeDuplication(origTopLeftNode);

      undoEntry.add(
        flowView.setNodePositions([{ nodeId: newNodeIdByOriginal[origTopLeftNode.id], position: newTopLeftPosition }]),
      );

      const delta = {
        x: newTopLeftPosition.x - origTopLeftNode.position.x,
        y: newTopLeftPosition.y - origTopLeftNode.position.y,
      };

      // Position rest of the nodes relative to the top-left node
      for (const node of originalNodesNotInGroups) {
        if (node.id === origTopLeftNode.id) {
          continue;
        }

        undoEntry.add(
          flowView.setNodePositions([
            {
              nodeId: newNodeIdByOriginal[node.id],
              position: {
                x: node.position.x + delta.x,
                y: node.position.y + delta.y,
              },
            },
          ]),
        );
      }

      return undoEntry;
    },
    [getCenterPosition, isNodeInViewport, groupManagerView, flowView],
  );

  const scrollNodeIntoViewIfNeeded = useCallback(
    (nodes: Node[]) => {
      if (nodes.length === 0) {
        return;
      }

      // Convert relative positions to absolute for nodes with parentId
      const nodesWithAbsolutePositions = nodes.map((node) => {
        if (node.parentId) {
          const parentNode = flowView.getNode(node.parentId);
          if (parentNode) {
            const absolutePosition = getAbsolutePositionFromRelative(node.position, parentNode);
            return {
              ...node,
              position: absolutePosition,
            };
          }
        }
        return node;
      });

      const areAllNodesInViewport = nodesWithAbsolutePositions.every((node) => isNodeInViewport(node));

      if (areAllNodesInViewport) {
        return;
      }

      // Get current viewport to preserve zoom
      const currentViewport = getViewport();

      // Calculate center position of all nodes in flow coordinates (using absolute positions)
      const nodesBounds = getNodesBounds(nodesWithAbsolutePositions);
      const centerX = nodesBounds.x + nodesBounds.width / 2;
      const centerY = nodesBounds.y + nodesBounds.height / 2;

      // Get container dimensions
      const containerRect = getReactFlowContainerRect();
      if (!containerRect) {
        return;
      }

      // Calculate screen center position
      const screenCenterX = (containerRect.right - containerRect.left) / 2;
      const screenCenterY = (containerRect.bottom - containerRect.top) / 2;

      // Calculate viewport position to center nodes on screen
      // Formula: screenX = flowX * zoom + viewportX
      // To center: screenCenterX = centerX * zoom + viewportX
      // Therefore: viewportX = screenCenterX - centerX * zoom
      const newViewportX = screenCenterX - centerX * currentViewport.zoom;
      const newViewportY = screenCenterY - centerY * currentViewport.zoom;

      // Pan to center without changing zoom - explicitly preserve zoom
      setViewport(
        {
          x: newViewportX,
          y: newViewportY,
          zoom: currentViewport.zoom,
        },
        { duration: 800 },
      );
    },
    [setViewport, getViewport, isNodeInViewport, getReactFlowContainerRect, flowView],
  );

  function findTopLeftNode(nodesWithAbsolutePositions: Node[]) {
    let topLeftNode = nodesWithAbsolutePositions[0];
    let minX = topLeftNode.position.x;
    let minY = topLeftNode.position.y;

    for (const node of nodesWithAbsolutePositions) {
      if (node.position.x < minX || (node.position.x === minX && node.position.y < minY)) {
        minX = node.position.x;
        minY = node.position.y;
        topLeftNode = node;
      }
    }
    return topLeftNode;
  }

  return { positionDuplicatedNodes, scrollNodeIntoViewIfNeeded };
}
